





































































































import { Component, Prop, Vue, Ref, Watch } from "vue-property-decorator";

import BaseMultiSelect from "./BaseMultiSelect.vue";
import Checkbox from "./MultiSelectCheckbox.vue";
import { Agency } from "@/models/MultiSelectBroadcaster";
import VIcon from "@/components/VIcon.vue";

export type MultiSelectGroup = {
  id: string | number;
  name: string;
  options?: MultiSelectOptions;
};

export type MultiSelectAction = {
  name: string;
  filter: (element: MultiSelectOption) => boolean;
};

export type MultiSelectActionState = {
  active: boolean;
  empty: boolean;
};

let instanceCount = 0;

export interface MultiSelectOption {
  advertising_agency?: Agency;
  id?: number;
  name: string;
  group?: number;
  date?: string;
  type?: string;
  nestLevel?: number;
  options?: MultiSelectOption[];
}

export type MultiSelectOptions = Array<MultiSelectOption>;

@Component({
  components: {
    Checkbox,
    BaseMultiSelect,
    VIcon,
  },
})
export default class MultiSelect extends Vue {
  @Prop() readonly value?: MultiSelectOptions;
  @Prop({ required: true }) readonly options!: MultiSelectOptions;
  @Prop({ required: false }) readonly actions?: Array<MultiSelectAction> | null;
  @Prop({ required: false }) readonly groups?: MultiSelectGroup[];
  @Prop() readonly groupBy?: Function;

  @Prop({ default: "Seleziona un valore" }) readonly placeholder!: string;
  @Prop({ default: true }) readonly clearItemsOnReselect!: boolean;

  @Prop({ default: false }) readonly disabled!: boolean;
  @Prop({ default: false }) readonly confirmSelection!: boolean;
  @Prop({ default: false }) readonly showDropdown!: boolean;
  @Prop({ type: Boolean, default: false }) readonly searchEnabled!: boolean;

  @Ref("upperSensor") upperSensor!: HTMLDivElement[];
  @Ref("lowerSensor") lowerSensor!: HTMLDivElement[];

  selected = this.value;
  maxElements = 50;
  offset = 0;
  search: any = null;
  dropdownVisible = false;
  actionIdPrefix = `multiselect-action-${++instanceCount}`;

  @Watch("value", { immediate: true })
  onValueUpdated(value: any) {
    this.selected = value;
  }

  @Watch("disabled")
  onDisabledUpdated(value: boolean) {
    if (value) {
      this.selected = [];
    }
  }

  @Watch("selected")
  onSelectedUpdated(value: any) {
    if (this.confirmSelection == false) {
      this.$emit("input", value);
    }
  }

  get isGrouped() {
    return this.groups && this.groups.length;
  }

  get groupedOptions() {
    return this.groups
      ?.map((group) => {
        group.options = this.getGroupedOptions(group);
        return group;
      })
      .filter((group) => {
        return (group.options?.length ?? 0) > 0;
      });
  }

  get filteredOptions() {
    if (this.search) {
      return this.options?.filter((option) => {
        return (
          option.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1
        );
      });
    } else {
      return this.options;
    }
  }

  actionId(index: number) {
    return `${this.actionIdPrefix}-${index}`;
  }

  getActionCssClass(index: number) {
    return {
      "list-inline-item": true,
      active: this.actionStates[index].active,
    };
  }

  // TODO: can be useful. Atm not used.
  // But for future implementation I leave it here for now.
  getOptionCssClass(option: MultiSelectOption) {
    // TODO: remove this forced return to activate it.
    return "";

    // if (!option?.nestLevel) return "";

    // switch (option.nestLevel) {
    //   case 1:
    //     return "first-nest-level";
    //   case 2:
    //     return "second-nest-level";
    //   case 3:
    //     return "third-nest-level";
    //   default:
    //     return "";
    // }
  }

  getGroupedOptions(group: any) {
    if (this.groupBy instanceof Function) {
      return this.groupBy(this.options, group);
    } else {
      return this.options?.filter((element) => {
        let valid = element.group == group.id;

        if (this.search) {
          valid =
            valid &&
            element.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1;
        }

        return valid;
      });
    }
  }

  onSelectAll() {
    this.selected = [];

    for (const option of this.filteredOptions ?? []) {
      this.selected.push(option);
    }
  }

  selectAll() {
    this.onSelectAll();
  }

  activateAction(action: MultiSelectAction) {
    if (!this.isActionActive(action)) {
      this.onAction(action);
    }
  }

  onSelectGroup(group: any) {
    const filtered = this.filteredOptions?.filter(
      (element) => element.group == group.id
    );
    const selectedSize = this.selected?.length;
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    //@ts-ignore
    this.selected = [...new Set([...this.selected, ...filtered])];
    if (selectedSize == this.selected.length) {
      const filteredSet = new Set(filtered);
      this.selected = this.selected.filter((x) => !filteredSet.has(x));
    }
  }

  onApply() {
    this.dropdownVisible = false;
    this.$emit("input", this.selected);
  }

  onCleared() {
    if (this.confirmSelection) {
      this.$emit("input", this.selected);
    }
  }

  onScroll(e: Event, holder: HTMLSpanElement) {
    if (!holder) {
      return;
    }
    if (!holder.getBoundingClientRect) {
      return;
    }
    const upperSensors = this.upperSensor;
    const lowerSensors = this.lowerSensor;

    if (upperSensors) {
      const holderRect = holder.getBoundingClientRect();

      for (let i = 0; i < upperSensors?.length; i++) {
        const sensor = upperSensors[i];
        const { top, bottom, height } = sensor.getBoundingClientRect();
        if (holderRect.top - top < 0) {
          this.offset = parseInt(sensor.getAttribute("data-index") ?? "");
          break;
        }
      }
    }

    if (lowerSensors) {
      const holderRect = holder.getBoundingClientRect();

      for (let i = lowerSensors.length - 1; i >= 0; i--) {
        const sensor = lowerSensors[i];
        const { top, bottom, height } = sensor.getBoundingClientRect();
        if (holderRect.bottom - top > 0) {
          this.offset = parseInt(sensor.getAttribute("data-index") ?? "");
          break;
        }
      }
    }
  }

  private matchingOptions(action: MultiSelectAction): MultiSelectOptions {
    return (this.options ?? []).filter(action.filter);
  }

  private selectedIdsOf(action: MultiSelectAction): Set<number | undefined> {
    return new Set(
      (this.selected ?? []).filter(action.filter).map((option) => option.id)
    );
  }

  get actionStates(): MultiSelectActionState[] {
    return (this.actions ?? []).map((action) => {
      const matching = this.matchingOptions(action);
      const selectedIds = this.selectedIdsOf(action);

      return {
        empty: matching.length === 0,
        active:
          matching.length > 0 &&
          matching.every((option) => selectedIds.has(option.id)),
      };
    });
  }

  isActionActive(action: MultiSelectAction): boolean {
    const index = (this.actions ?? []).indexOf(action);
    return index >= 0 ? this.actionStates[index].active : false;
  }

  onAction(action: MultiSelectAction) {
    const matching = this.matchingOptions(action);

    if (!matching.length) {
      return;
    }

    if (this.isActionActive(action)) {
      const matchingIds = new Set(matching.map((option) => option.id));
      this.selected = (this.selected ?? []).filter(
        (element) => !(action.filter(element) && matchingIds.has(element.id))
      );
    } else {
      const selectedIds = this.selectedIdsOf(action);
      this.selected = [
        ...(this.selected ?? []),
        ...matching.filter((option) => !selectedIds.has(option.id)),
      ];
    }
  }

  onSearch(term: string) {
    this.search = term;

    // reset virtual scroll window
    this.offset = 0;

    // move scrollbar to top
    this.$nextTick(() => {
      const scrollContainer = this.$el.querySelector(".list-options");
      if (scrollContainer && scrollContainer.parentElement) {
        scrollContainer.parentElement.scrollTop = 0;
      }
    });
  }

  onToggleVisible(visible: boolean) {
    if (visible) {
      this.offset = 0;
    }

    this.dropdownVisible = visible;
  }
}
